import { useCallback } from "react";
import { useAppAuth } from "@/auth/use-app-auth";

export type AuthMode = "login" | "signup";

type StartAuthArgs = {
  mode: AuthMode;
  returnTo?: string;
  orgAuthId?: string | null;
  connection?: string;
};

const AUTH_RETURN_TO_STORAGE_KEY = "therecruit.auth.returnTo";
const AUTH_INVITATION_TOKEN_STORAGE_KEY = "therecruit.auth.invitationToken";

export const getOrgFromLocation = (loc: Location = window.location): string | null => {
  return new URLSearchParams(loc.search).get("org");
};

export const persistAuthReturnTo = (returnTo?: string) => {
  if (typeof window === "undefined" || !returnTo) return;
  window.sessionStorage.setItem(AUTH_RETURN_TO_STORAGE_KEY, returnTo);
};

export const readAuthReturnTo = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(AUTH_RETURN_TO_STORAGE_KEY);
};

export const clearAuthReturnTo = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(AUTH_RETURN_TO_STORAGE_KEY);
};

// Pages a brand-new account may open before organization onboarding. The Apex 25 demo results
// are the reason someone signed up; onboarding starts when they go on into the app.
const BEFORE_ONBOARDING = /^\/demo\/interview\/[0-9a-f-]{36}$/i;
export const opensBeforeOnboarding = (returnTo: string) => BEFORE_ONBOARDING.test(returnTo);

export const persistInvitationToken = (token?: string) => {
  if (typeof window === "undefined" || !token) return;
  window.sessionStorage.setItem(AUTH_INVITATION_TOKEN_STORAGE_KEY, token);
};

export const readInvitationToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(AUTH_INVITATION_TOKEN_STORAGE_KEY);
};

export const clearInvitationToken = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(AUTH_INVITATION_TOKEN_STORAGE_KEY);
};

/**
 * Centralized Clerk redirect helper for consistent org-aware flows.
 */
export const useAuthFlow = () => {
  const { loginWithRedirect } = useAppAuth();

  return useCallback(
    async ({ mode, returnTo, orgAuthId, connection }: StartAuthArgs) => {
      const inferredOrg = orgAuthId ?? getOrgFromLocation();
      const authorizationParams: Record<string, any> = {
        redirect_uri: `${window.location.origin}/auth-callback`,
      };
      persistAuthReturnTo(returnTo);

      if (mode === "signup") {
        authorizationParams.screen_hint = "signup";
      }
      if (inferredOrg) {
        authorizationParams.organization = inferredOrg;
      }
      if (connection) {
        authorizationParams.connection = connection;
      }

      const appState = returnTo ? { returnTo } : undefined;

      await loginWithRedirect({
        authorizationParams,
        appState,
      });
    },
    [loginWithRedirect]
  );
};
